import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { useStagePlay } from '@/hooks/useStagePlay';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { RequesterAvatar } from './RequesterAvatar';
import {
  AI_AGENT_PAYLOADS,
  FINAL_STAGE,
  LOOP_HOLD_MS,
  payloadForStage,
  STEP_DURATION_MS,
  storylineKeyForStage,
} from './stageLogic';
import styles from './PlatformSection.module.css';
import animStyles from './PlatformSection.animations.module.css';

/* 9-step narrative — strictly follows docs/7 core abilities orchaschain.md
 * ─────────────────────────────────────────────────────────────────────
 *  s1 Title ghost
 *  s2 Title particles fly to sides, coalesce into NovarPass (blue, left)
 *     + NovarVault (purple, right); connection lines start
 *  s3 Org avatar enters at bottom center; three request-pulse rings expand
 *  s4 Flow line from Pass + flow line from Vault → NovarConsent ✓ badge
 *     descends onto avatar head
 *  s5 Two NovarAnchor gold dots pop on the flow lines (mid-line,
 *     left + right), joined by a faint gold tether — three-way binding
 *  s6 Big NovarLCM ring expands to wrap everything, starts spinning;
 *     NovarAIScan card appears on the right
 *  s7 Dashed pulse comm from ring → AIAgent; payload rotates through
 *     expiry check / renewal / policy diff / anchor verify / anomaly scan
 *  s8 Payload reaches "audit" → green Audit pulse fires inside ring
 *     (3 rings expand + NovarAudit badge); AIAgent card highlights
 *  s9 LCM keeps spinning ~2 more turns → fade out → loop back to s1
 * ───────────────────────────────────────────────────────────────────── */

export const PlatformSection: FC = () => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const { prefersReducedMotion } = useReducedMotion();
  const { stage } = useStagePlay(rootRef, {
    finalStage: FINAL_STAGE,
    stepDuration: STEP_DURATION_MS,
    pauseAfterStage: LOOP_HOLD_MS / 1000,
    visibility: 0.25,
    loop: true,
  });

  const titleVisible = stage === 1;
  const particleVisible = stage === 2;
  const cardsVisible = stage >= 2;
  const avatarVisible = stage >= 3;
  const consentVisible = stage === 4 || stage === 5;
  const anchorVisible = stage >= 5;
  const lcmVisible = stage >= 6;
  const aiAgentVisible = stage >= 6;
  const commVisible = stage >= 7;
  const auditVisible = stage >= 8;

  const storylineKey = storylineKeyForStage(stage);
  const aiPayload = payloadForStage(stage);
  const payloadIndex = AI_AGENT_PAYLOADS.indexOf(aiPayload);
  const isAuditPayload = aiPayload === 'audit';

  return (
    <Section tone="muted" id="synergy">
      <Container size="wide">
        <div ref={rootRef} className={styles.root} data-stage={stage}>
          <header className={styles.header}>
            <Eyebrow>{t('home.synergy.kicker')}</Eyebrow>
            <h2 className={styles.title}>{t('home.synergyAnimation.title')}</h2>
            <p className={styles.lede}>{t('home.synergyAnimation.subline')}</p>
          </header>

          <div className={styles.stage} aria-live="polite">
            {/* ─── s1: title ghost ─── */}
            <p
              className={[styles.titleGhost, titleVisible ? animStyles.titleShatter : ''].join(' ')}
              style={{ opacity: titleVisible ? 1 : 0 }}
            >
              {t('home.synergyAnimation.title')}
            </p>

            {/* ─── s2: title particles ─── */}
            {particleVisible && (
              <div className={styles.particles} aria-hidden="true">
                {Array.from({ length: 12 }).map((_, i) => (
                  <span
                    key={i}
                    className={[
                      styles.particle,
                      i < 6 ? styles.particleLeft : styles.particleRight,
                    ].join(' ')}
                    style={{ animationDelay: `${i * 60}ms` }}
                  />
                ))}
              </div>
            )}

            {/* ─── s6+: LCM ring wraps everything ─── */}
            <div
              className={[styles.lcmRing, lcmVisible ? animStyles.lcmSpin : ''].join(' ')}
              style={{ opacity: lcmVisible ? 1 : 0 }}
              aria-hidden="true"
            >
              <span className={styles.lcmLabel}>NovarLCM</span>
            </div>

            {/* ─── Connection lines from Pass/Vault to Avatar ─── */}
            {cardsVisible && (
              <svg
                className={styles.linesLayer}
                viewBox="0 0 560 440"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                {/* Pass (top-left, blue) → Avatar (center) */}
                <path
                  d="M 110 200 Q 195 290 280 340"
                  className={[styles.linePath, styles.lineBlue].join(' ')}
                />
                <circle r="5" className={[styles.lineDot, styles.lineDotBlue].join(' ')}>
                  <animateMotion
                    dur="2s"
                    repeatCount="indefinite"
                    path="M 110 200 Q 195 290 280 340"
                  />
                </circle>
                {/* Vault (top-right, purple) → Avatar (center) */}
                <path
                  d="M 450 200 Q 365 290 280 340"
                  className={[styles.linePath, styles.linePurple].join(' ')}
                />
                <circle r="5" className={[styles.lineDot, styles.lineDotPurple].join(' ')}>
                  <animateMotion
                    dur="2s"
                    repeatCount="indefinite"
                    path="M 450 200 Q 365 290 280 340"
                  />
                </circle>
              </svg>
            )}

            {/* ─── s2: NovarPass card (blue, left) ─── */}
            <article
              className={[
                styles.capCard,
                styles.capCardPass,
                cardsVisible ? animStyles.cardEnterLeft : '',
              ].join(' ')}
              style={{ opacity: cardsVisible ? 1 : 0 }}
            >
              <p className={styles.capName}>NovarPass</p>
              <p className={styles.capPurpose}>Establish trust identity</p>
              <div className={styles.capTags}>
                <span className={styles.capTag}>passport</span>
                <span className={styles.capTag}>diploma</span>
              </div>
            </article>

            {/* ─── s2: NovarVault card (purple, right) ─── */}
            <article
              className={[
                styles.capCard,
                styles.capCardVault,
                cardsVisible ? animStyles.cardEnterRight : '',
              ].join(' ')}
              style={{ opacity: cardsVisible ? 1 : 0 }}
            >
              <p className={styles.capName}>NovarVault</p>
              <p className={styles.capPurpose}>Store trust assets</p>
              <div className={styles.capTags}>
                <span className={styles.capTag}>contract</span>
                <span className={styles.capTag}>invoice</span>
              </div>
            </article>

            {/* ─── s5: NovarAnchor gold dots on flow lines + tether ─── */}
            {anchorVisible && <div className={styles.anchorTether} aria-hidden="true" />}
            <div
              className={[
                styles.anchorSlot,
                styles.anchorSlotLeft,
                anchorVisible ? animStyles.anchorPop : '',
              ].join(' ')}
              style={{ opacity: anchorVisible ? 1 : 0 }}
              aria-hidden="true"
            >
              <span className={styles.anchorDot} />
              <span className={styles.anchorLabel}>NovarAnchor</span>
            </div>
            <div
              className={[
                styles.anchorSlot,
                styles.anchorSlotRight,
                anchorVisible ? animStyles.anchorPop : '',
              ].join(' ')}
              style={{ opacity: anchorVisible ? 1 : 0 }}
              aria-hidden="true"
            >
              <span className={styles.anchorDot} />
              <span className={styles.anchorLabel}>NovarAnchor</span>
            </div>

            {/* ─── s4-5: NovarConsent ✓ badge landing on avatar ─── */}
            <div
              className={[styles.consentBadge, consentVisible ? animStyles.consentLand : ''].join(
                ' ',
              )}
              style={{ opacity: consentVisible ? 1 : 0 }}
              aria-hidden="true"
            >
              <span className={styles.consentDot}>✓</span>
              <span>NovarConsent</span>
            </div>

            {/* ─── s3: Org & Personal avatar (bottom center) ─── */}
            <div className={styles.avatarSlot} style={{ opacity: avatarVisible ? 1 : 0 }}>
              <RequesterAvatar isActive={stage === 3} />
              <span className={styles.avatarLabel}>Organization</span>
            </div>

            {/* ─── s6: NovarAIScan card (right side, with payload pill) ─── */}
            <article
              className={[
                styles.capCard,
                styles.capCardAIAgent,
                aiAgentVisible ? animStyles.cardEnterRight : '',
                isAuditPayload && auditVisible ? styles.capCardHighlight : '',
              ].join(' ')}
              style={{ opacity: aiAgentVisible ? 1 : 0 }}
            >
              <p className={styles.capName}>NovarAIScan</p>
              <p className={styles.capPurpose}>Monitor &amp; answer</p>
              <div className={styles.capTags}>
                <span className={styles.capTag}>policy diff</span>
                <span className={styles.capTag}>anomaly</span>
              </div>
            </article>

            {/* ─── s7+: dashed pulse comm line from LCM → AIAgent ─── */}
            {commVisible && (
              <div className={styles.aiComm} aria-hidden="true">
                <span className={styles.aiCommDash} />
                <span
                  key={`payload-${payloadIndex}-${stage}`}
                  className={[
                    styles.aiCommPayload,
                    isAuditPayload ? styles.aiCommPayloadAudit : '',
                  ].join(' ')}
                >
                  {aiPayload}
                </span>
                <span className={styles.aiCommDash} />
              </div>
            )}

            {/* ─── s8: Audit pulse fires inside ring ─── */}
            <div
              className={[styles.auditPulse, auditVisible ? animStyles.auditFire : ''].join(' ')}
              style={{ opacity: auditVisible ? 1 : 0 }}
              aria-hidden="true"
            >
              <span className={styles.auditRing} />
              <span className={styles.auditRing} />
              <span className={styles.auditRing} />
              <span className={styles.auditBadge}>NovarAudit</span>
            </div>

            {/* ─── Storyline caption ─── */}
            <p className={styles.storyline} style={{ opacity: storylineKey ? 1 : 0 }}>
              {storylineKey ? t(`home.synergyAnimation.stages.${storylineKey}`) : ''}
            </p>
          </div>

          {!prefersReducedMotion && (
            <p className={styles.a11yHint} aria-hidden="true">
              {t('home.synergyAnimation.subline')}
            </p>
          )}
        </div>
      </Container>
    </Section>
  );
};

PlatformSection.displayName = 'PlatformSection';
